import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { RH_ACTIONS } from '@shared/constants/rh-actions.constants';
import { SesionService } from '@intelishs-group/session-core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { EmpleadoService } from '@services/empleado.service';
import { VacacionesService } from '@services/vacaciones.service';
import {
  SolicitudVacaciones,
  DirectorAsignacion,
  EmpleadoEquipoSaldo,
} from '@schemas/vacaciones.schema';
import {
  getEstatusColor as _getEstatusColor,
  getResultadoTexto as _getResultadoTexto,
  getResultadoTipo as _getResultadoTipo,
  getResultadoMotivo as _getResultadoMotivo,
} from '@shared/vacaciones.helpers';
import { SolicitudDetalleModal } from '../../components/solicitud-detalle-modal/solicitud-detalle-modal';
import { SolicitudAprobarModal } from '../../components/solicitud-aprobar-modal/solicitud-aprobar-modal';

type FiltroEstado = 'todos' | 'pendientes' | 'por-vencer';

import {
  CellStack,
  EmptyState,
  StatChip,
  PageHeader,
  TabLabel,
  TableActions,
  type ActionButtonDef,
  DataTable,
  CellTemplateDirective,
  type ColumnDef,
  type TableAction,
} from '@intelishs-group/ui';

@Component({
  selector: 'app-mis-autorizaciones',
  imports: [
    TabLabel,
    DatePipe,
    DecimalPipe,
    FormsModule,
    NzSelectModule,
    NzIconModule,
    NzButtonModule,
    NzTabsModule,
    NzBadgeModule,
    NzTooltipModule,
    DataTable,
    CellTemplateDirective,
    TableActions,
    PageHeader,
    EmptyState,
    CellStack,
    StatChip,
    SolicitudDetalleModal,
    SolicitudAprobarModal,
  ],
  templateUrl: './mis-autorizaciones.page.html',
  styleUrl: './mis-autorizaciones.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MisAutorizacionesPage implements OnInit {
  private readonly vacacionesService = inject(VacacionesService);
  private readonly empleadoService = inject(EmpleadoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sesion = inject(SesionService);
  private readonly message = inject(NzMessageService);

  // ── Director ───────────────────────────────────────────────────────
  directores = signal<DirectorAsignacion[]>([]);
  directorSeleccionado = signal<DirectorAsignacion | null>(null);

  /**
   * Rol del portal SSO (ej. "Administrador RH", "Director RH").
   * Llenado desde useSessionService() / SessionFallback en ngOnInit.
   */

  /**
   * True si el usuario puede operar sobre otros directores (alta administrativa
   * de RH/Admin). Director ve solo lo suyo; selector oculto.
   */
  /**
   * ¿Puede ver las autorizaciones de OTRO director?
   *
   * Antes: `toRolCanonico(rol) === 'Admin' || === 'RH'`. Ahora pregunta por el
   * MODULO otorgado, que es el mismo conjunto de personas —`empleados` lo tiene
   * solo `administrador-rh`— sin depender de como este escrito el nombre del rol
   * en cada ambiente. Lo preciso seria una FUNCION; ver la nota gemela en
   * `solicitar-vacaciones.page.ts`.
   */
  readonly puedeSeleccionarOtro = computed<boolean>(() => this.sesion.tieneModulo('empleados'));

  /** ID del empleado logueado (`kidempleadorh`) — para hacer match con la lista de directores. */
  private readonly miEmpleadoId = signal<number | null>(null);

  // ── Solicitudes pendientes ─────────────────────────────────────────
  solicitudes = signal<SolicitudVacaciones[]>([]);
  loading = signal(false);
  totalPendientes = computed(() => this.solicitudes().length);

  /** Pestaña activa: 0 = Mi equipo, 1 = Solicitudes pendientes. */
  tabActiva = signal(0);

  /**
   * Empleado al que se acota la pestaña de pendientes cuando se llega desde
   * "Mi equipo" con varias solicitudes. `null` = todas.
   */
  empleadoFiltro = signal<{ id: number; nombre: string } | null>(null);

  /**
   * Pendientes ordenadas por empleado y, dentro de cada uno, por fecha de
   * inicio: con varias solicitudes por persona (vacaciones simultáneas), las
   * de un mismo colaborador se leen juntas y en el orden en que ocurren.
   */
  solicitudesVisibles = computed(() => {
    const f = this.empleadoFiltro();
    return this.solicitudes()
      .filter((s) => !f || s.empleadoId === f.id)
      .sort(
        (a, b) =>
          a.empleadoNombre.localeCompare(b.empleadoNombre, 'es') ||
          a.fechaInicio.localeCompare(b.fechaInicio),
      );
  });

  // ── Mi equipo ──────────────────────────────────────────────────────
  equipo = signal<EmpleadoEquipoSaldo[]>([]);
  loadingEquipo = signal(false);
  filtroEstado = signal<FiltroEstado>('todos');

  equipoFiltrado = computed(() => {
    let items = this.equipo();
    const estado = this.filtroEstado();

    if (estado === 'pendientes') {
      items = items.filter((e) => e.solicitudesPendientes.length > 0);
    } else if (estado === 'por-vencer') {
      items = items.filter((e) => e.periodoAnterior && this.diasHastaVencimientoEmp(e) <= 30);
    }
    return items;
  });

  equipoStats = computed(() => {
    const e = this.equipo();
    return {
      total: e.length,
      criticos: e.filter((x) => x.periodoAnterior && this.diasHastaVencimientoEmp(x) <= 10).length,
      porVencer: e.filter((x) => x.periodoAnterior && this.diasHastaVencimientoEmp(x) <= 30).length,
      conPendientes: e.filter((x) => x.solicitudesPendientes.length > 0).length,
    };
  });

  // ── Modal aprobación ───────────────────────────────────────────────
  modalVisible = signal(false);
  modalAccion = signal<'aprobar' | 'rechazar'>('aprobar');
  solicitudSeleccionada = signal<SolicitudVacaciones | null>(null);

  // ── Modal detalle solicitud ────────────────────────────────────────
  detalleVisible = signal(false);
  detalleEmpleado = signal<SolicitudVacaciones | null>(null);
  /** true cuando el detalle muestra solo el saldo sin solicitud asociada */
  detalleEsSoloSaldo = signal(false);

  // ── Columnas Tab 1: Mi Equipo ──────────────────────────────────────
  // La columna "Pendientes" volvió con las vacaciones simultáneas: un
  // empleado puede tener varias solicitudes esperando al director, y el
  // número ya no es siempre 0 o 1.
  /**
   * Panel de saldos del equipo. Aquí NO se compactan las columnas de ciclo:
   * son bloques visuales (barra de progreso, contador de vencimiento) y
   * apilarlos los volvería ilegibles. Solo se unifica la identificación del
   * empleado y se anclan los dos extremos.
   *
   * Sin columna de estado —el estado del empleado se lee en los propios
   * bloques de saldo—, así que a la izquierda se ancla la columna que
   * identifica la fila, que es la que hace de referencia al scrollear.
   */
  equipoColumns: ColumnDef<EmpleadoEquipoSaldo>[] = [
    {
      key: 'empleadoNombre',
      header: 'Empleado',
      width: '270px',
      type: 'custom',
      // El `cell-stack` no trunca solo: sin el flag el nombre largo ensancha la
      // columna y desborda la tabla. Llega al wrapper `.celda-empleado` porque
      // declara `min-width: 0`.
      ellipsis: true,
      filterable: true,
    },
    { key: 'fechaIngreso', header: 'Ingreso', width: '100px', align: 'center', type: 'date' },
    // `headerAlign` y no `align`: el título va centrado sobre la columna, pero
    // el contenido son bloques de varias líneas y una barra de progreso, que se
    // leen mejor alineados a la izquierda.
    {
      key: 'periodoAnterior',
      header: 'Ciclo anterior',
      width: '135px',
      headerAlign: 'center',
      type: 'custom',
    },
    {
      key: 'periodoActual',
      header: 'Ciclo actual',
      width: '130px',
      headerAlign: 'center',
      type: 'custom',
    },
    // El ancho lo forzaba el encabezado, no el dato: "Total disponible" mide
    // más que el "9,9" que hay debajo. Con las dos columnas de ciclo al lado,
    // "Total" se lee sin ambigüedad —es la suma de ambos—.
    {
      key: 'diasDisponiblesTotal',
      header: 'Total',
      width: '75px',
      align: 'center',
      type: 'custom',
    },
    {
      key: 'solicitudesPendientes',
      header: 'Pendientes',
      width: '95px',
      align: 'center',
      type: 'custom',
    },
    // "Aprobadas" basta: la columna vive en el panel de vacaciones del equipo,
    // así que el sustantivo se sobreentiende y el encabezado dejaba de ser lo
    // más ancho de la columna.
    {
      key: 'proximasVacaciones',
      header: 'Aprobadas',
      // `'auto'` explicito: vacio solo funciona mientras la tabla sea fluida.
      width: 'auto',
      headerAlign: 'center',
      // SIN `ellipsis` a proposito, y es la excepcion de esta pagina: la celda
      // apila N `cell-stack` (una por periodo aprobado). Recortar a un renglon
      // escondería del segundo en adelante, que es perder datos, no compactar.
      type: 'custom',
    },
    // Hasta 3 botones de 24px + 2 gaps = 84px más el padding de celda.
    {
      key: 'acciones',
      header: 'Acciones',
      width: '100px',
      align: 'center',
      type: 'custom',
    },
  ];

  /** ID y puesto viven en la línea meta del empleado. */
  readonly equipoFilterKeys = ['idInterno', 'puesto'];

  /**
   * Tiñe la fila del equipo según lo cerca que esté de vencerse el periodo anterior.
   *
   * Las clases las publica el theme (≥ 4.24.17). Antes eran locales —`equipo-row--critico` y
   * `--advertencia`— con sus reglas en el `styles.css` GLOBAL y un `!important` cada una, y las dos
   * cosas eran necesarias: el `<tr>` lo renderiza el `data-table`, así que el CSS de esta página no
   * lo alcanza (de ahí el global), y `tr.equipo-row--critico td` queda en especificidad (0,1,2)
   * contra el hover de ng-zorro en (0,3,2), así que sin `!important` el color se perdía bajo el
   * cursor (el orden de carga solo rompe empates, no diferencias de especificidad).
   *
   * Las del theme declaran el estado también en `:hover` con la cadena a (0,5,2), así que ganan por
   * especificidad: el color sigue igual de FIJO que antes y ya no hace falta `!important`.
   */
  equipoRowClass = (row: EmpleadoEquipoSaldo) => ({
    'sinay-fila-critica': this.urgenciaEmp(row) === 'critico',
    'sinay-fila-advertencia': this.urgenciaEmp(row) === 'advertencia',
  });

  /**
   * Con UNA pendiente, aprobar y rechazar van directo a ella desde la fila.
   * Con VARIAS, la fila no puede saber cuál: el ojo lleva a la pestaña de
   * pendientes acotada a ese empleado, donde cada una tiene sus botones y se
   * resuelve por separado. Antes se actuaba siempre sobre la primera.
   */
  getActionsForEquipo(emp: EmpleadoEquipoSaldo): ActionButtonDef[] {
    const pendientes = emp.solicitudesPendientes;
    if (pendientes.length > 1) {
      return [
        { ...RH_ACTIONS.ver, tooltip: `Ver sus ${pendientes.length} solicitudes pendientes` },
      ];
    }
    const actions: ActionButtonDef[] = [
      {
        ...RH_ACTIONS.ver,
        tooltip: pendientes.length > 0 ? 'Ver solicitud y saldo' : 'Ver saldo de vacaciones',
      },
    ];
    if (pendientes.length === 1) {
      actions.push(
        { ...RH_ACTIONS.aprobar, tooltip: `Aprobar ${pendientes[0].diasSolicitados} días` },
        RH_ACTIONS.rechazar,
      );
    }
    return actions;
  }

  /** Abre la pestaña de pendientes acotada a un empleado. */
  verPendientesDe(emp: EmpleadoEquipoSaldo): void {
    this.empleadoFiltro.set({ id: emp.empleadoId, nombre: emp.empleadoNombre });
    this.tabActiva.set(1);
  }

  onEquipoAction(event: TableAction<EmpleadoEquipoSaldo>): void {
    switch (event.action) {
      case 'ver':
        this.abrirDetalleEquipo(event.row);
        break;
      case 'aprobar':
        this.abrirAprobacionEquipo(event.row, 'aprobar');
        break;
      case 'rechazar':
        this.abrirAprobacionEquipo(event.row, 'rechazar');
        break;
    }
  }

  // ── Columnas Tab 2: Solicitudes Pendientes ─────────────────────────
  /** Mismo tratamiento que el tab RH de autorizaciones: de 9 columnas a 4. */
  solicitudesColumns: ColumnDef<SolicitudVacaciones>[] = [
    // Sin `width` a propósito: es la columna de texto variable y en
    // `table-layout: fixed` las columnas sin ancho se reparten el sobrante.
    {
      key: 'empleadoNombre',
      header: 'Empleado',
      width: 'auto',
      type: 'custom',
      ellipsis: true,
      filterable: true,
      sortable: true,
    },
    {
      key: 'fechaInicio',
      header: 'Periodo',
      width: '200px',
      type: 'custom',
      ellipsis: true,
      sortable: true,
    },
    {
      key: 'fechaSolicitud',
      header: 'Solicitada',
      width: '115px',
      align: 'center',
      type: 'date',
      sortable: true,
    },
    {
      key: 'acciones',
      header: 'Acciones',
      width: '100px',
      align: 'center',
      type: 'custom',
    },
  ];

  /** Campos que pasaron a la línea meta del empleado. */
  readonly solicitudesFilterKeys = ['empleadoIdInterno', 'area', 'puesto'];

  readonly solicitudesActions: ActionButtonDef[] = [
    RH_ACTIONS.ver,
    RH_ACTIONS.aprobar,
    RH_ACTIONS.rechazar,
  ];

  onSolicitudAction(event: TableAction<SolicitudVacaciones>): void {
    switch (event.action) {
      case 'ver':
        this.detalleEsSoloSaldo.set(false);
        this.abrirDetalle(event.row);
        break;
      case 'aprobar':
        this.abrirModal(event.row, 'aprobar');
        break;
      case 'rechazar':
        this.abrirModal(event.row, 'rechazar');
        break;
    }
  }

  // ── Inicialización ─────────────────────────────────────────────────
  ngOnInit(): void {
    // La sesión YA está resuelta: `proveerSesion` la carga antes de montar el
    // primer componente, así que no hay «camino feliz» ni fallback que esperar.
    this.aplicarSesion();
  }

  /**
   * Bifurca el flujo según rol:
   * - Admin / RH → carga la lista completa de directores; el selector queda
   *   visible para revisar pendientes de cualquier director. Si el usuario
   *   logueado coincide con alguno, lo autoselecciona como default.
   * - Director → carga la lista, pero busca su propio empleado (vía
   *   /api/Empleado/me) y autoselecciona su DirectorAsignacion; selector oculto.
   */
  private aplicarSesion(): void {
    // Siempre cargamos la lista — Admin/RH la necesita en el dropdown;
    // Director la usa para hacer el match consigo mismo.
    this.vacacionesService
      .getDirectores()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.directores.set(data);
          this.intentarAutoseleccion();
        },
      });

    // Todos los roles que pueden entrar a esta pantalla (Admin, RH, Director)
    // necesitan resolver su empleado para autoseleccionarse. Si /me falla,
    // Admin/RH puede seguir eligiendo manualmente; un Director sin perfil
    // de empleado vinculado verá un mensaje claro.
    this.empleadoService
      .getMe()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (emp) => {
          if (emp?.id) {
            this.miEmpleadoId.set(emp.id);
            this.intentarAutoseleccion();
          }
        },
        error: () => {
          if (!this.puedeSeleccionarOtro()) {
            this.message.error(
              'Tu cuenta no está vinculada a un perfil de empleado en RH. ' +
                'Contacta a sistemas para que se complete tu alta.',
              { nzDuration: 8000 },
            );
          }
          // Admin/RH sigue pudiendo elegir manualmente; silencio.
        },
      });
  }

  /**
   * Cuando ya tenemos lista de directores y empleado logueado, intenta
   * cruzar ambos. Si encuentra al usuario en la lista de directores,
   * lo autoselecciona y dispara la carga de su equipo/pendientes.
   * No hace nada si alguno de los dos aún no está poblado.
   */
  private intentarAutoseleccion(): void {
    const id = this.miEmpleadoId();
    const lista = this.directores();
    if (id === null || lista.length === 0) return;
    if (this.directorSeleccionado() !== null) return; // ya hay selección manual

    const match = lista.find((d) => d.directorId === id);
    if (match) {
      this.onDirectorChange(match.directorId);
    }
  }

  onDirectorChange(directorId: number): void {
    const director = this.directores().find((d) => d.directorId === directorId) ?? null;
    this.directorSeleccionado.set(director);
    if (director) {
      this.cargar();
      this.cargarEquipo();
    } else {
      this.solicitudes.set([]);
      this.equipo.set([]);
    }
  }

  // ── Carga solicitudes pendientes ───────────────────────────────────
  cargar(): void {
    const director = this.directorSeleccionado();
    if (!director) return;
    this.loading.set(true);

    // Backend ahora devuelve `directorEmpleadoIds` con TODOS los subordinados
    // directos (vía movempleadojerarquia), tanto para DG como para director
    // de área. Lo usamos siempre que venga; `areas` queda solo como fallback.
    const subordinados = director.directorEmpleadoIds ?? [];

    if (subordinados.length) {
      this.vacacionesService
        .getPendientesParaDG(subordinados)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            this.solicitudes.set(data);
            this.loading.set(false);
          },
          error: () => this.loading.set(false),
        });
    } else {
      const areas = director.areas;
      if (!areas.length) {
        this.loading.set(false);
        return;
      }
      this.vacacionesService
        .getPendientesPorAreas(areas)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            // Excluir al propio director de las solicitudes que debe aprobar
            this.solicitudes.set(data.filter((s) => s.empleadoId !== director.directorId));
            this.loading.set(false);
          },
          error: () => this.loading.set(false),
        });
    }
  }

  // ── Carga saldo del equipo ─────────────────────────────────────────
  cargarEquipo(): void {
    const director = this.directorSeleccionado();
    if (!director) return;
    this.loadingEquipo.set(true);

    // Igual que `cargar`: preferir IDs directos sobre filtro por área.
    const subordinados = director.directorEmpleadoIds ?? [];
    const options = subordinados.length
      ? { empleadoIds: subordinados }
      : { areas: director.areas, excludeEmpleadoId: director.directorId };

    this.vacacionesService
      .getEquipoSaldo(options)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.equipo.set(data);
          this.loadingEquipo.set(false);
        },
        error: () => this.loadingEquipo.set(false),
      });
  }

  // ── Helpers urgencia ───────────────────────────────────────────────
  diasHastaVencimientoEmp(emp: EmpleadoEquipoSaldo): number {
    if (!emp.periodoAnterior) return 9999;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    // Tolera "YYYY-MM-DD" y "YYYY-MM-DDTHH:mm:ss" — antes el concat rompía
    // el formato backend (Invalid Date → NaN días).
    const venc = new Date(emp.periodoAnterior.fechaVencimiento);
    if (isNaN(venc.getTime())) return 9999;
    return Math.ceil((venc.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
  }

  urgenciaEmp(emp: EmpleadoEquipoSaldo): 'critico' | 'advertencia' | null {
    if (!emp.periodoAnterior) return null;
    const dias = this.diasHastaVencimientoEmp(emp);
    if (dias <= 10) return 'critico';
    if (dias <= 30) return 'advertencia';
    return null;
  }

  // ── Acciones desde la tabla de equipo ─────────────────────────────
  abrirDetalleEquipo(emp: EmpleadoEquipoSaldo): void {
    if (emp.solicitudesPendientes.length > 1) {
      this.verPendientesDe(emp);
    } else if (emp.solicitudesPendientes.length === 1) {
      this.detalleEsSoloSaldo.set(false);
      this.abrirDetalle(emp.solicitudesPendientes[0]);
    } else {
      this.detalleEsSoloSaldo.set(true);
      this.detalleEmpleado.set({
        id: 0,
        empleadoId: emp.empleadoId,
        empleadoNombre: emp.empleadoNombre,
        empleadoIdInterno: emp.idInterno,
        area: emp.area,
        puesto: emp.puesto,
        fechaInicio: '',
        fechaFin: '',
        diasSolicitados: 0,
        estatus: 'Pendiente',
        fechaSolicitud: '',
      });
      this.detalleVisible.set(true);
    }
  }

  abrirAprobacionEquipo(emp: EmpleadoEquipoSaldo, accion: 'aprobar' | 'rechazar'): void {
    // Solo se ofrece con una sola pendiente (ver getActionsForEquipo).
    if (emp.solicitudesPendientes.length !== 1) return;
    this.abrirModal(emp.solicitudesPendientes[0], accion);
  }

  abrirModal(solicitud: SolicitudVacaciones, accion: 'aprobar' | 'rechazar'): void {
    this.solicitudSeleccionada.set(solicitud);
    this.modalAccion.set(accion);
    this.modalVisible.set(true);
  }

  solicitudGeneraSaldoNegativo(empleadoId: number): boolean {
    // Deuda real, no total a hoy < 0: una solicitud a futuro que se cubre a
    // su fecha deja el total negativo sin que falte nada.
    const emp = this.equipo().find((e) => e.empleadoId === empleadoId);
    return emp?.tieneSaldoNegativo ?? false;
  }

  abrirDetalle(sol: SolicitudVacaciones): void {
    this.detalleEmpleado.set(sol);
    this.detalleVisible.set(true);
  }

  onAprobacionProcesada(): void {
    this.cargar();
    this.cargarEquipo();
  }

  getProximasVacacionesVisibles(emp: EmpleadoEquipoSaldo): SolicitudVacaciones[] {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const ayer = new Date(hoy);
    ayer.setDate(ayer.getDate() - 1);
    const ayerStr = ayer.toISOString().slice(0, 10);
    return emp.proximasVacaciones.filter((v) => v.fechaFin >= ayerStr);
  }

  // ── Helpers ────────────────────────────────────────────────────────
  getEstatusColor = _getEstatusColor;
  getResultadoTexto = _getResultadoTexto;
  getResultadoTipo = _getResultadoTipo;
  getResultadoMotivo = _getResultadoMotivo;
}
